import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Gift, ShieldCheck, Loader2 } from "lucide-react";
import { ASSETS } from "@/data/content";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGES = ["1–2 роки", "2–3 роки", "3–4 роки", "4–5 років", "5–6 років", "6–7 років"];

const inputCls =
  "w-full rounded-2xl bg-white/[0.05] border border-amber-400/20 px-5 py-3.5 text-sm sm:text-base text-white placeholder:text-slate-400/70 outline-none focus:border-amber-300/70 focus:bg-white/[0.08] focus:shadow-[0_0_20px_rgba(230,184,92,0.15)] transition-all duration-300";

export default function LeadForm({ selected, setSelected }) {
  const [form, setForm] = useState({ parent_name: "", child_name: "", child_age: "", phone: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Оберіть садочок для вашої дитини");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/applications`, { ...form, kindergarten: selected });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18463703849/WQpXCNHe-YcdEKmGl-RE",
          value: 1.0,
          currency: "EUR",
        });
        if (typeof window.gtagSendEvent === "function") window.gtagSendEvent();
      }
      toast.success("Заявку відправлено! Ми зв’яжемося з вами найближчим часом ✨");
      setForm({ parent_name: "", child_name: "", child_age: "", phone: "" });
    } catch (err) {
      toast.error("Не вдалося відправити заявку. Спробуйте ще раз або зателефонуйте нам.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="forma" className="relative py-16 sm:py-24 px-4 overflow-hidden" data-testid="lead-form-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_60%_at_50%_50%,rgba(230,184,92,0.08),transparent)]" />
      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Eyebrow number="10" text="Запис" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-testid="lead-form-title">
            ВАША ДИТИНА ЗАСЛУГОВУЄ НА <span className="text-gold-gradient">НАЙКРАЩИЙ ПОЧАТОК</span>
          </h2>
          <div className="mt-7 flex flex-wrap gap-3" data-testid="discount-badges">
            <div
              className="inline-flex items-center gap-2.5 rounded-full border border-amber-300/50 bg-amber-300/12 px-5 py-2.5 shadow-[0_0_25px_rgba(230,184,92,0.25)]"
              data-testid="discount-note"
            >
              <Gift className="w-4 h-4 text-amber-300" />
              <span className="text-sm sm:text-base text-amber-100 font-medium">При заповненні форми — знижка 5%</span>
            </div>
            <div
              className="inline-flex items-center gap-2.5 rounded-full border border-amber-300/50 bg-amber-300/12 px-5 py-2.5 shadow-[0_0_25px_rgba(230,184,92,0.25)]"
              data-testid="discount-note-military"
            >
              <ShieldCheck className="w-4 h-4 text-amber-300" />
              <span className="text-sm sm:text-base text-amber-100 font-medium">Для військових — знижка 10%</span>
            </div>
          </div>
          <div className="mt-10 hidden lg:flex items-center gap-5 opacity-90">
            <img src={ASSETS.parkLogo} alt="Park Kids Club" className="w-20 h-20 rounded-3xl object-cover bg-white animate-float-soft" loading="lazy" decoding="async" />
            <img src={ASSETS.royalLogo} alt="Royal Kids Club" className="w-20 h-20 rounded-3xl object-cover animate-float-soft" style={{ animationDelay: "1.2s" }} loading="lazy" decoding="async" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="glass rounded-[2rem] p-6 sm:p-9 shadow-[0_25px_70px_rgba(0,0,0,0.5)]"
            data-testid="lead-form"
          >
            <div className="space-y-4">
              <input
                required
                value={form.parent_name}
                onChange={set("parent_name")}
                placeholder="Ім’я батьків"
                className={inputCls}
                data-testid="form-parent-name-input"
              />
              <input
                required
                value={form.child_name}
                onChange={set("child_name")}
                placeholder="Ім’я дитини"
                className={inputCls}
                data-testid="form-child-name-input"
              />
              <select
                required
                value={form.child_age}
                onChange={set("child_age")}
                className={`${inputCls} cursor-pointer ${form.child_age ? "" : "text-slate-400/70"}`}
                data-testid="form-child-age-select"
              >
                <option value="" disabled className="bg-[#0a211a]">Вік дитини</option>
                {AGES.map((a) => (
                  <option key={a} value={a} className="bg-[#0a211a]">{a}</option>
                ))}
              </select>
              <input
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Номер телефону"
                className={inputCls}
                data-testid="form-phone-input"
              />

              <div className="grid grid-cols-2 gap-3" data-testid="kindergarten-selector">
                {[
                  { id: "park", name: "Park Kids Club", logo: ASSETS.parkLogo },
                  { id: "royal", name: "Royal Kids Club", logo: ASSETS.royalLogo },
                ].map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => setSelected(k.id)}
                    className={`rounded-2xl border px-3 py-3.5 flex items-center gap-2.5 transition-all duration-300 cursor-pointer ${
                      selected === k.id
                        ? "border-amber-300 bg-amber-300/15 shadow-[0_0_22px_rgba(230,184,92,0.3)]"
                        : "border-amber-400/20 bg-white/[0.04] hover:border-amber-300/50"
                    }`}
                    data-testid={`form-kindergarten-${k.id}`}
                  >
                    <img src={k.logo} alt={k.name} className="w-8 h-8 rounded-full object-cover bg-white shrink-0" loading="lazy" decoding="async" />
                    <span className="text-xs sm:text-sm text-white font-medium text-left leading-tight">{k.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              className="group mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-bold px-8 py-4 text-sm sm:text-base shadow-[0_0_35px_rgba(230,184,92,0.55)] hover:shadow-[0_0_50px_rgba(230,184,92,0.85)] transition-shadow duration-300 disabled:opacity-60 cursor-pointer"
              data-testid="application-form-submit-button"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  ВІДПРАВИТИ ЗАЯВКУ
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
